import { useFetcher, useNavigation } from '@remix-run/react';
import localforage from 'localforage';
import { useEffect, useState } from 'react';
import { useIsLoggedIn } from './use-is-logged-in';
import type { Feed } from '~/services/layout/index';
import { isClientStorageAvailable } from '~/utils/local-storage.client';
import { useLocalStorageSuffix } from './use-local-storage-suffix';

export function useLoadFeed(apiEndpoint: string, enabled: boolean): void {
  const fetcher = useFetcher({
    key: apiEndpoint,
  });

  const navigation = useNavigation();

  const { isLoggedIn } = useIsLoggedIn();

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    if (fetcher.state === 'idle' && !fetcher.data) {
      fetcher.load(`/api/${apiEndpoint}`);
    }

    return () => {
      if (navigation.state === 'loading' && fetcher.data)
        fetcher.data = undefined;
    };
  }, [isLoggedIn, enabled, navigation.state, fetcher.state]);
}

export function useGetStorageFeed({
  storageKey,
  condition,
  isLoggedIn,
}: {
  storageKey: 'continue-watching' | 'favorites';
  condition: boolean;
  isLoggedIn: boolean | null;
}): {
  feed: Feed | undefined;
  feedCount: number;
  isLoading: boolean;
  isUpdating: boolean;
} {
  const fetcher: any = useFetcher({
    key: storageKey,
  });

  const navigation = useNavigation();

  const [feed, setFeed] = useState<Feed | undefined>(undefined);

  const localStorageSuffix = useLocalStorageSuffix();

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!condition || !localStorageSuffix || fetcher.state !== 'idle') return;

    const getAndSetFeedData = async () => {
      if (!isClientStorageAvailable()) return;

      const feedData = (await localforage.getItem(
        `${storageKey}-${localStorageSuffix}`
      )) as Feed;

      setFeed(feedData || []);
    };

    getAndSetFeedData();
  }, [
    condition,
    fetcher.state,
    fetcher.data,
    localStorageSuffix,
    navigation.state,
  ]);

  const feedCount: number = feed?.entry?.length || 0;
  const isLoading: boolean = fetcher.state === 'loading';
  const isUpdating: boolean = isLoading && feedCount > 0;

  return {
    feed,
    feedCount,
    isLoading,
    isUpdating,
  };
}
